import { SubscriptionStatus } from '@viome/viome-base/src/usecase/Misc/MiscModel';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { isEligibleForComponentization } from '@/utils/componentizationEligibility';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';

/**
 * Whether the current user should see the componentized (per-formula) Shop
 * flow. Gated by the `enable_componentization` Statsig public_config kill
 * switch. First-time buyers (no active non-KIT subscription yet) are always
 * eligible; existing subscribers are gated by the SKU allowlist in
 * isEligibleForComponentization — ALL of their active non-KIT subscriptions
 * must be eligible, not just one. A single legacy/ineligible SKU disqualifies
 * the whole account into the legacy Shop experience, even if their other
 * subscriptions are otherwise eligible.
 *
 * `isMultiFormulaSelectUser` distinguishes the subset of componentization users who
 * already have one or more active eligible subscriptions — these users get
 * the checkbox multi-select bulk-add flow instead of the single radio-select
 * flow used by first-time (zero subscription) buyers.
 */
export interface UseIsComponentizationUserResult {
  isComponentizationUser: boolean;
  isMultiFormulaSelectUser: boolean;
  isLoading: boolean;
}

export const useIsComponentizationUser = (): UseIsComponentizationUserResult => {
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();

  const { data, isLoading: isSubscriptionStatusLoading } =
    useGetSubscriptionStatusQuery();

  if (!isComponentizationEnabled) {
    return { isComponentizationUser: false, isMultiFormulaSelectUser: false, isLoading: false };
  }

  if (isSubscriptionStatusLoading) {
    return { isComponentizationUser: false, isMultiFormulaSelectUser: false, isLoading: true };
  }

  const activeNonKitSubscriptions =
    data?.subscriptions?.filter(
      (subscription) =>
        subscription.category !== 'KIT' &&
        subscription.subscription_status === SubscriptionStatus.Active,
    ) ?? [];

  if (activeNonKitSubscriptions.length === 0) {
    return { isComponentizationUser: true, isMultiFormulaSelectUser: false, isLoading: false };
  }

  const allSubscriptionsEligible = activeNonKitSubscriptions.every((subscription) =>
    isEligibleForComponentization(subscription, eligibleSkus),
  );

  return {
    isComponentizationUser: allSubscriptionsEligible,
    isMultiFormulaSelectUser: allSubscriptionsEligible,
    isLoading: false,
  };
};
